// /opt/isp_erp/frontend/src/pages/admin/finanzas/promesas/PromesasList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { promesasApi } from '../../../../api/finanzas/promesas.api';
import { format } from 'date-fns';
import './PromesasList.css';

export default function PromesasList() {
  const navigate = useNavigate();
  const [promesas, setPromesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    estado: '',
    cliente: ''
  });

  const estados = [
    { value: 'PENDIENTE', label: 'Pendientes', color: 'yellow' },
    { value: 'PARCIAL', label: 'Parciales', color: 'orange' },
    { value: 'CUMPLIDA', label: 'Cumplidas', color: 'green' },
    { value: 'INCUMPLIDA', label: 'Incumplidas', color: 'red' },
    { value: 'RENEGOCIADA', label: 'Renegociadas', color: 'blue' },
    { value: 'ANULADA', label: 'Anuladas', color: 'gray' }
  ];

  const promesasFiltradas = useMemo(() => {
    let filtradas = promesas;

    if (filtros.estado) {
      filtradas = filtradas.filter(p => p.estado === filtros.estado);
    }

    if (filtros.cliente) {
      const term = filtros.cliente.toLowerCase().trim();
      filtradas = filtradas.filter(p =>
        p.cliente_nombre?.toLowerCase().includes(term)
      );
    }

    return filtradas;
  }, [promesas, filtros]);

  useEffect(() => {
    cargarPromesas();
  }, []);

  const cargarPromesas = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      const response = await promesasApi.listar(params);
      setPromesas(response.data);
    } catch (error) {
      console.error('Error cargando promesas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return format(new Date(fecha), 'dd/MM/yyyy');
    } catch {
      return 'Fecha inválida';
    }
  };

  if (loading) {
    return (
      <div className="promesas-loading-container">
        <div className="promesas-loading-spinner"></div>
        <p>Cargando promesas de pago...</p>
      </div>
    );
  }

  return (
    <div className="promesas-container">
      {/* Header */}
      <div className="promesas-header">
        <div className="promesas-header-left">
          <div className="promesas-header-icon">📅</div>
          <div>
            <h1>Promesas de Pago</h1>
            <p>Gestión de promesas y acuerdos de pago</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/finanzas/promesas/nueva')}
          className="promesas-btn-primary"
        >
          ➕ Nueva Promesa
        </button>
      </div>

      {/* Filtros */}
      <div className="promesas-filtros">
        <div className="filtros-grid">
          <select
            value={filtros.estado}
            onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
            className="filtro-select"
          >
            <option value="">Todos los estados</option>
            {estados.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>

          <div className="filtro-busqueda">
            <input
              type="text"
              placeholder="Buscar por nombre de cliente..."
              value={filtros.cliente}
              onChange={(e) => setFiltros({...filtros, cliente: e.target.value})}
              className="filtro-input"
            />
            {filtros.cliente && (
              <button
                onClick={() => setFiltros({...filtros, cliente: ''})}
                className="filtro-clear"
                title="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>

          <button
            onClick={() => setFiltros({ estado: '', cliente: '' })}
            className="filtro-btn-limpiar"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="promesas-table-container">
        <table className="promesas-table">
          <thead>
            <tr>
              <th>N° Promesa</th>
              <th>Cliente</th>
              <th>Fecha Acuerdo</th>
              <th>Vencimiento</th>
              <th className="text-right">Monto Total</th>
              <th className="text-right">Pagado</th>
              <th className="text-right">Saldo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {promesasFiltradas.length === 0 ? (
              <tr>
                <td colSpan="9" className="promesas-empty">
                  <div className="empty-icon">📅</div>
                  <p className="empty-title">No hay promesas de pago registradas</p>
                  <p className="empty-subtitle">
                    Crea una nueva promesa para comenzar a gestionar acuerdos de pago
                  </p>
                </td>
              </tr>
            ) : (
              promesasFiltradas.map(p => (
                <tr key={p.id} className="promesa-row">
                  <td className="promesa-numero">{p.numero_promesa}</td>
                  <td className="promesa-cliente">{p.cliente_nombre}</td>
                  <td className="promesa-fecha">{formatearFecha(p.fecha_acuerdo)}</td>
                  <td className="promesa-fecha">{formatearFecha(p.fecha_limite)}</td>
                  <td className="promesa-monto text-right">${p.monto_total?.toFixed(2)}</td>
                  <td className="promesa-pagado text-right">${p.monto_pagado?.toFixed(2)}</td>
                  <td className="promesa-saldo text-right">${p.saldo_pendiente?.toFixed(2)}</td>
                  <td>
                    <span className={`promesa-estado estado-${p.estado?.toLowerCase()}`}>
                      {p.estado}
                    </span>
                  </td>
                  <td className="promesa-acciones">
                    <button
                      onClick={() => navigate(`/finanzas/promesas/${p.id}`)}
                      className="promesa-btn-ver"
                      title="Ver detalle de promesa"
                    >
                      Ver Detalle
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Estadísticas rápidas */}
      {promesasFiltradas.length > 0 && (
        <div className="promesas-stats">
          <div className="stats-card">
            <span className="stats-label">Total promesado</span>
            <span className="stats-value">
              ${promesasFiltradas.reduce((sum, p) => sum + (p.monto_total || 0), 0).toFixed(2)}
            </span>
          </div>
          <div className="stats-card">
            <span className="stats-label">Total pagado</span>
            <span className="stats-value">
              ${promesasFiltradas.reduce((sum, p) => sum + (p.monto_pagado || 0), 0).toFixed(2)}
            </span>
          </div>
          <div className="stats-card">
            <span className="stats-label">Saldo pendiente</span>
            <span className="stats-value">
              ${promesasFiltradas.reduce((sum, p) => sum + (p.saldo_pendiente || 0), 0).toFixed(2)}
            </span>
          </div>
          <div className="stats-card">
            <span className="stats-label">Promesas activas</span>
            <span className="stats-value">
              {promesasFiltradas.filter(p => p.estado === 'PENDIENTE' || p.estado === 'PARCIAL').length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
